import { useEffect, type ChangeEvent } from 'react';
import { useFetcher } from 'react-router-dom';
import { FormField, Input } from '@dma/ui';
import './ImageUploadField.css';

interface UploadActionSuccess {
  ok: true;
  url: string;
}
interface UploadActionFailure {
  ok: false;
  message: string;
}
type UploadActionData = UploadActionSuccess | UploadActionFailure;

export interface ImageUploadFieldProps {
  /** Label shown above the field. */
  label: string;
  /** Unique id, so multiple instances on one page (e.g. a repeatable image list) don't collide. */
  fieldId: string;
  /** Current image URL — either typed by hand or filled in after a successful upload. */
  value: string;
  onChange: (url: string) => void;
  /**
   * Storage sub-folder for this field's uploads (e.g. 'banner', 'articles',
   * 'scientific-content') — keeps each feature's files grouped in the
   * `site-media` bucket rather than all dumped in one flat folder.
   */
  folder: string;
  disabled?: boolean;
}

/**
 * Wraps the plain "paste a URL" input every admin form already had with an
 * optional file picker that uploads to Supabase Storage (via the
 * `/admin/media/upload` action — see app/routes.tsx) and fills the URL in
 * automatically. The URL field is kept, not replaced: an admin who already
 * has an image hosted elsewhere can still just paste its address.
 */
export function ImageUploadField({ label, fieldId, value, onChange, folder, disabled }: ImageUploadFieldProps) {
  const fetcher = useFetcher<UploadActionData>();
  const isUploading = fetcher.state !== 'idle';

  useEffect(() => {
    if (fetcher.data?.ok) {
      onChange(fetcher.data.url);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- only re-run when a new upload result arrives, not on every onChange identity change
  }, [fetcher.data]);

  function handleFileSelected(event: ChangeEvent<HTMLInputElement>) {
    const file = event.target.files?.[0];
    event.target.value = ''; // allow re-selecting the same file later
    if (!file) return;

    const formData = new FormData();
    formData.append('file', file);
    formData.append('folder', folder);
    fetcher.submit(formData, {
      method: 'post',
      action: '/admin/media/upload',
      encType: 'multipart/form-data'
    });
  }

  return (
    <FormField label={label} htmlFor={fieldId}>
      <div className="dma-image-upload">
        <Input
          id={fieldId}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder="https://... یا یک تصویر آپلود کنید"
          disabled={disabled}
        />
        <label className="dma-image-upload__button">
          {isUploading ? 'در حال آپلود...' : 'انتخاب تصویر از سیستم'}
          <input
            type="file"
            accept="image/jpeg,image/png,image/webp,image/gif,image/svg+xml"
            onChange={handleFileSelected}
            disabled={disabled || isUploading}
          />
        </label>
      </div>

      {fetcher.data && !fetcher.data.ok ? (
        <p className="dma-image-upload__error" role="alert">
          {fetcher.data.message}
        </p>
      ) : null}

      {value ? (
        <img src={value} alt="" className="dma-image-upload__preview" />
      ) : null}
    </FormField>
  );
}
